/* ============================================================
   File:    assets/theme.js
   Purpose: Theme interactivity:
            - Mobile menu, sticky header
            - Hero slideshow
            - Product gallery + variant picker
            - AJAX cart (drawer + add/remove/update)
            - Quantity selectors
   ============================================================ */

(function () {
  'use strict';

  // ========================================================
  // 1. UTILITIES
  // ========================================================
  const $  = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));

  const routes = (window.AquaOn && window.AquaOn.routes) || {};

  const formatMoney = (cents) => {
    const amount = (cents / 100).toFixed(2);
    return '$' + amount;
  };

  const fetchJSON = (url, options = {}) =>
    fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Accept':       'application/json',
        ...(options.headers || {})
      }
    }).then((r) => r.json());

  // ========================================================
  // 2. HEADER — sticky shadow + mobile menu
  // ========================================================
  const Header = {
    init() {
      const header = $('.header');
      if (!header) return;

      const onScroll = () => {
        header.classList.toggle('is-scrolled', window.scrollY > 10);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();

      const toggle = $('.header__menu-toggle');
      const nav    = $('.header__nav');
      if (toggle && nav) {
        toggle.addEventListener('click', () => {
          nav.classList.toggle('is-open');
          const isOpen = nav.classList.contains('is-open');
          toggle.setAttribute('aria-expanded', isOpen);
          // Close all dropdowns when hamburger closes the nav
          if (!isOpen) closeAllDropdowns();
        });
      }

      // ---- Dropdown menus ----
      const dropdownItems = $$('.header__nav-item--has-dropdown');

      function closeAllDropdowns(except) {
        dropdownItems.forEach((item) => {
          if (item === except) return;
          item.classList.remove('is-open');
          const btn = $('[data-dropdown-toggle]', item);
          if (btn) btn.setAttribute('aria-expanded', 'false');
        });
      }

      dropdownItems.forEach((item) => {
        const btn = $('[data-dropdown-toggle]', item);
        if (!btn) return;

        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const isOpen = item.classList.toggle('is-open');
          btn.setAttribute('aria-expanded', isOpen);
          if (isOpen) closeAllDropdowns(item);
        });
      });

      // Close dropdowns when clicking outside
      document.addEventListener('click', (e) => {
        if (!e.target.closest('.header__nav-item--has-dropdown')) {
          closeAllDropdowns();
        }
      });

      // Close dropdowns on Escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') closeAllDropdowns();
      });
    }
  };

  // ========================================================
  // 3. HERO SLIDESHOW
  // ========================================================
  const HeroSlideshow = {
    init() {
      $$('.hero-slideshow').forEach((slideshow) => this.setup(slideshow));
    },

    setup(slideshow) {
      const track  = $('.hero-slideshow__track', slideshow);
      const slides = $$('.hero-slide', slideshow);
      const dots   = $$('.hero-slideshow__dot', slideshow);
      const prev   = $('.hero-slideshow__arrow--prev', slideshow);
      const next   = $('.hero-slideshow__arrow--next', slideshow);

      if (!track || slides.length <= 1) return;

      const autoplay  = slideshow.dataset.autoplay === 'true';
      const interval  = parseInt(slideshow.dataset.interval || '5000', 10);
      let current     = 0;
      let timer       = null;

      const go = (i) => {
        current = (i + slides.length) % slides.length;
        track.style.transform = `translateX(-${current * 100}%)`;
        dots.forEach((d, idx) => d.classList.toggle('is-active', idx === current));
      };

      const start = () => {
        if (!autoplay) return;
        stop();
        timer = setInterval(() => go(current + 1), interval);
      };
      const stop = () => { if (timer) { clearInterval(timer); timer = null; } };

      dots.forEach((dot, idx) => dot.addEventListener('click', () => { go(idx); start(); }));
      prev && prev.addEventListener('click', () => { go(current - 1); start(); });
      next && next.addEventListener('click', () => { go(current + 1); start(); });

      slideshow.addEventListener('mouseenter', stop);
      slideshow.addEventListener('mouseleave', start);

      start();
    }
  };

  // ========================================================
  // 4. TESTIMONIAL CAROUSEL
  // ========================================================
  const TestimonialCarousel = {
    init() {
      $$('.testimonial-carousel').forEach((c) => this.setup(c));
    },

    visibleFor(width) {
      if (width <= 560) return 1;
      if (width <= 900) return 2;
      return 3;
    },

    setup(carousel) {
      const track = $('.testimonial-carousel__track', carousel);
      const cards = $$('.testimonial-card', carousel);
      const dotsWrap = $('.testimonial-carousel__dots', carousel);
      const controls = $('.testimonial-carousel__controls', carousel);
      const prev = $('.testimonial-carousel__arrow--prev', carousel);
      const next = $('.testimonial-carousel__arrow--next', carousel);

      if (!track || cards.length === 0) return;

      const autoplay = carousel.dataset.autoplay === 'true';
      const interval = parseInt(carousel.dataset.interval || '6000', 10);
      let current = 0;
      let visible = this.visibleFor(window.innerWidth);
      let maxIndex = Math.max(0, cards.length - visible);
      let timer = null;

      const gap = () => {
        const g = getComputedStyle(track).columnGap || getComputedStyle(track).gap || '0';
        return parseFloat(g) || 0;
      };

      const step = () => cards[0].getBoundingClientRect().width + gap();

      const go = (i) => {
        current = Math.max(0, Math.min(i, maxIndex));
        track.style.transform = `translateX(-${current * step()}px)`;
        if (dotsWrap) {
          $$('.testimonial-carousel__dot', dotsWrap)
            .forEach((d, idx) => d.classList.toggle('is-active', idx === current));
        }
      };

      // Build one dot per scroll position (rebuilt on resize).
      const buildDots = () => {
        if (!dotsWrap) return;
        dotsWrap.innerHTML = '';
        for (let i = 0; i <= maxIndex; i++) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'testimonial-carousel__dot' + (i === current ? ' is-active' : '');
          dot.setAttribute('aria-label', `Go to testimonial group ${i + 1}`);
          dot.addEventListener('click', () => { go(i); start(); });
          dotsWrap.appendChild(dot);
        }
      };

      const start = () => {
        if (!autoplay || maxIndex === 0) return;
        stop();
        timer = setInterval(() => {
          // Roll forward one card, wrapping back to the start at the end.
          go(current >= maxIndex ? 0 : current + 1);
        }, interval);
      };
      const stop = () => { if (timer) { clearInterval(timer); timer = null; } };

      const recalc = () => {
        visible = this.visibleFor(window.innerWidth);
        maxIndex = Math.max(0, cards.length - visible);
        if (current > maxIndex) current = maxIndex;
        // Hide controls when everything already fits.
        if (controls) controls.style.display = maxIndex === 0 ? 'none' : '';
        buildDots();
        go(current);
      };

      prev && prev.addEventListener('click', () => { go(current - 1); start(); });
      next && next.addEventListener('click', () => { go(current + 1); start(); });

      carousel.addEventListener('mouseenter', stop);
      carousel.addEventListener('mouseleave', start);

      let resizeTimer = null;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(recalc, 150);
      }, { passive: true });

      recalc();
      start();
    }
  };

  // ========================================================
  // 5. PRODUCT GALLERY
  // ========================================================
  const ProductGallery = {
    init() {
      const gallery = $('.product-gallery');
      if (!gallery) return;

      const main   = $('#ProductMainImage', gallery) || $('.product-gallery__main img', gallery);
      const thumbs = $$('.product-gallery__thumb', gallery);
      const track  = $('[data-gallery-track]', gallery);
      const prev   = $('[data-gallery-prev]', gallery);
      const next   = $('[data-gallery-next]', gallery);
      const models = $$('.product-gallery__model', gallery);
      const btn3d  = $('[data-show-model]', gallery);

      // 3D media: model-viewer is only downloaded the first time a model is opened
      const loadModelViewer = () => {
        if (customElements.get('model-viewer') || document.querySelector('script[data-aq-mv]')) return;
        const s = document.createElement('script');
        s.type = 'module';
        s.src = 'https://ajax.googleapis.com/ajax/libs/model-viewer/4.0.0/model-viewer.min.js';
        s.setAttribute('data-aq-mv', '');
        document.head.appendChild(s);
      };
      const setActiveThumb = (thumb) => {
        thumbs.forEach((t) => t.classList.toggle('is-active', t === thumb));
      };
      const showModel = (id) => {
        loadModelViewer();
        models.forEach((m) => { m.hidden = m.dataset.modelId !== String(id); });
        if (main) main.hidden = true;
        if (btn3d) btn3d.hidden = true;
        setActiveThumb(thumbs.find((t) => t.dataset.modelId === String(id)));
      };
      const showImage = (src, thumb) => {
        models.forEach((m) => { m.hidden = true; });
        if (main) { main.hidden = false; if (src) main.src = src; }
        if (btn3d) btn3d.hidden = false;
        setActiveThumb(thumb);
      };

      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          if (thumb.dataset.modelId) showModel(thumb.dataset.modelId);
          else showImage(thumb.dataset.fullSrc, thumb);
        });
      });
      if (btn3d) btn3d.addEventListener('click', () => showModel(btn3d.dataset.showModel));

      // Slider behaviour: scroll by visible width, disable buttons at edges.
      if (!track || !prev || !next) return;

      const updateArrows = () => {
        const maxScroll = track.scrollWidth - track.clientWidth;
        prev.disabled = track.scrollLeft <= 1;
        next.disabled = track.scrollLeft >= maxScroll - 1;
        // Hide both if all thumbs fit
        const hide = track.scrollWidth <= track.clientWidth + 1;
        prev.style.display = hide ? 'none' : '';
        next.style.display = hide ? 'none' : '';
      };

      const scrollByPage = (dir) => {
        // Scroll by ~80% of the visible track width for a comfortable step
        track.scrollBy({ left: dir * track.clientWidth * 0.8, behavior: 'smooth' });
      };

      prev.addEventListener('click', () => scrollByPage(-1));
      next.addEventListener('click', () => scrollByPage(1));
      track.addEventListener('scroll', updateArrows, { passive: true });
      window.addEventListener('resize', updateArrows);
      // Run once after layout settles
      requestAnimationFrame(updateArrows);
    }
  };

  // ========================================================
  // 5. VARIANT PICKER
  // ========================================================
  const VariantPicker = {
    init() {
      const form = $('[data-product-form]');
      if (!form) return;
      this.form     = form;
      this.product  = JSON.parse($('[data-product-json]').textContent);
      this.selected = {};

      // Initialize from default variant
      const defaultVariant = this.product.variants.find((v) => v.available) || this.product.variants[0];
      if (defaultVariant) {
        defaultVariant.options.forEach((opt, idx) => {
          this.selected[`option${idx + 1}`] = opt;
        });
      }

      $$('.variant-picker__option', form).forEach((btn) => {
        if (btn.dataset.value === this.selected[`option${parseInt(btn.dataset.optionIndex, 10) + 1}`]) {
          btn.classList.add('is-active');
        }
        btn.addEventListener('click', () => this.onOptionClick(btn));
      });

      this.updateUI();
    },

    onOptionClick(btn) {
      const optionIndex = parseInt(btn.dataset.optionIndex, 10);
      const value       = btn.dataset.value;
      this.selected[`option${optionIndex + 1}`] = value;

      $$(`.variant-picker__option[data-option-index="${optionIndex}"]`, this.form)
        .forEach((b) => b.classList.toggle('is-active', b.dataset.value === value));

      const labelSpan = $(`.variant-picker[data-option-index="${optionIndex}"] .variant-picker__label-value`, this.form);
      if (labelSpan) labelSpan.textContent = value;

      this.updateUI();
    },

    findVariant() {
      return this.product.variants.find((v) => {
        return v.options.every((opt, idx) => opt === this.selected[`option${idx + 1}`]);
      });
    },

    updateUI() {
      const variant     = this.findVariant();
      const idInput     = $('[name="id"]', this.form);
      const priceEl     = $('[data-product-price]');
      const comparePrice= $('[data-product-compare-price]');
      const submitBtn   = $('[data-add-to-cart]', this.form);

      if (!variant) {
        if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = window.AquaOn.strings.unavailable; }
        return;
      }

      if (idInput) idInput.value = variant.id;

      if (priceEl) priceEl.textContent = formatMoney(variant.price);
      if (comparePrice) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          comparePrice.textContent = formatMoney(variant.compare_at_price);
          comparePrice.style.display = '';
        } else {
          comparePrice.style.display = 'none';
        }
      }

      if (submitBtn) {
        if (variant.available) {
          submitBtn.disabled = false;
          submitBtn.textContent = submitBtn.dataset.addText || 'Add to cart';
        } else {
          submitBtn.disabled = true;
          submitBtn.textContent = window.AquaOn.strings.soldOut;
        }
      }

      // Update URL
      if (history.replaceState) {
        const url = new URL(window.location);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);
      }
    }
  };

  // ========================================================
  // 6. QUANTITY SELECTORS
  // ========================================================
  const QuantitySelectors = {
    init() {
      // Product-page selector only. The cart drawer's +/- buttons are handled
      // by event delegation in Cart.bindCartUpdates() because the drawer markup
      // is replaced on every AJAX refresh and would otherwise lose its listeners.
      $$('.quantity-selector').forEach((q) => {
        const minus = $('[data-qty-minus]', q);
        const plus  = $('[data-qty-plus]', q);
        const input = $('input', q);
        if (!input) return;

        minus && minus.addEventListener('click', () => {
          const val = Math.max(1, parseInt(input.value, 10) - 1);
          input.value = val;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
        plus && plus.addEventListener('click', () => {
          input.value = parseInt(input.value, 10) + 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }
  };

  // ========================================================
  // 7. CART — AJAX add, drawer open/close, line updates
  // ========================================================
  const Cart = {
    init() {
      this.drawer        = $('.cart-drawer');
      this.drawerOverlay = $('.cart-drawer__overlay');
      this.drawerBody    = $('.cart-drawer__body');
      this.drawerCount   = $('.header__cart-count');

      // Open/close
      $$('[data-cart-toggle]').forEach((btn) =>
        btn.addEventListener('click', (e) => { e.preventDefault(); this.toggle(); })
      );
      $$('[data-cart-close]').forEach((btn) =>
        btn.addEventListener('click', () => this.close())
      );
      this.drawerOverlay && this.drawerOverlay.addEventListener('click', () => this.close());
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });

      // Add to cart (any product form)
      $$('[data-product-form]').forEach((form) => {
        form.addEventListener('submit', (e) => this.onAddToCart(e, form));
      });

      // Cart page: update qty / remove
      this.bindCartUpdates();
    },

    bindCartUpdates() {
      // Quantity typed directly into a line input (cart page or drawer).
      document.addEventListener('change', (e) => {
        const input = e.target.closest('[data-cart-qty-input]');
        if (!input) return;
        this.updateLine(input.dataset.lineKey, parseInt(input.value, 10));
      });

      // Drawer +/- buttons — delegated so they keep working after the drawer
      // markup is replaced by an AJAX refresh.
      document.addEventListener('click', (e) => {
        const stepBtn = e.target.closest('.cart-drawer__qty [data-qty-plus], .cart-drawer__qty [data-qty-minus]');
        if (!stepBtn) return;
        const wrap  = stepBtn.closest('.cart-drawer__qty');
        const input = wrap && wrap.querySelector('input');
        if (!input) return;
        const isPlus = stepBtn.hasAttribute('data-qty-plus');
        let val = parseInt(input.value, 10);
        if (isNaN(val)) val = 0;
        val = isPlus ? val + 1 : Math.max(0, val - 1);
        input.value = val;
        this.updateLine(input.dataset.lineKey, val);
      });

      document.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('[data-cart-remove]');
        if (!removeBtn) return;
        e.preventDefault();
        this.updateLine(removeBtn.dataset.lineKey, 0);
      });
      // Buy Now: add to cart, then go straight to checkout.
      document.addEventListener('click', (e) => {
        const buyBtn = e.target.closest('[data-buy-now]');
        if (!buyBtn) return;
        e.preventDefault();
        const form = buyBtn.closest('[data-product-form]');
        if (form) this.onBuyNow(buyBtn, form);
      });
    },

    toggle() {
      if (!this.drawer) return;
      if (this.drawer.classList.contains('is-open')) {
        this.close();
      } else {
        this.open();
      }
    },

    open() {
      if (!this.drawer || this.drawer.classList.contains('is-open')) return;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.drawerOverlay && this.drawerOverlay.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
    },

    close() {
      if (!this.drawer || !this.drawer.classList.contains('is-open')) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.drawerOverlay && this.drawerOverlay.classList.remove('is-visible');
      document.body.style.overflow = '';
    },

    async onAddToCart(e, form) {
      e.preventDefault();
      const submitBtn = $('[data-add-to-cart]', form);
      const originalText = submitBtn ? submitBtn.textContent : '';
      if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Adding...'; }

      try {
        const formData = new FormData(form);
        const res = await fetch(routes.cart_add_url + '.js', {
          method: 'POST',
          body: formData,
          headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
        });

        if (!res.ok) throw new Error('Add to cart failed');
        await this.refresh();
        this.open();
      } catch (err) {
        console.error('Cart error:', err);
        alert(window.AquaOn.strings.cartError || 'Unable to add to cart.');
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = originalText;
        }
      }
    },

    async onBuyNow(buyBtn, form) {
      const originalText = buyBtn.textContent;
      buyBtn.disabled = true;
      buyBtn.textContent = 'Processing...';

      try {
        const formData = new FormData(form);
        const res = await fetch(routes.cart_add_url + '.js', {
          method: 'POST',
          body: formData,
          headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
        });

        if (!res.ok) throw new Error('Add to cart failed');
        // Send the shopper straight to checkout.
        window.location.href = '/checkout';
      } catch (err) {
        console.error('Buy now error:', err);
        alert(window.AquaOn.strings.cartError || 'Unable to start checkout.');
        buyBtn.disabled = false;
        buyBtn.textContent = originalText;
      }
    },

    async updateLine(lineKey, quantity) {
      try {
        const res = await fetchJSON(routes.cart_change_url + '.js', {
          method: 'POST',
          body: JSON.stringify({ id: lineKey, quantity })
        });
        await this.refresh();

        // If on cart page, reload to reflect new totals/lines accurately
        if (document.body.classList.contains('template--cart')) {
          window.location.reload();
        }
      } catch (err) {
        console.error('Update line error:', err);
      }
    },

    async refresh() {
      // Pull a fresh, layout-free render of the drawer and swap body + footer.
      try {
        const res  = await fetch(routes.cart_url + '?view=drawer', {
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        const html = await res.text();
        const tmp  = document.createElement('div');
        tmp.innerHTML = html;

        const newBody = tmp.querySelector('[data-cart-drawer-body]');
        const oldBody = this.drawerBody || $('[data-cart-drawer-body]');
        if (newBody && oldBody) oldBody.innerHTML = newBody.innerHTML;

        // The footer wrapper is always present now, so this reliably restores
        // the Checkout button the first time an item is added.
        const newFooter = tmp.querySelector('[data-cart-drawer-footer]');
        const oldFooter = $('[data-cart-drawer-footer]');
        if (newFooter && oldFooter) {
          oldFooter.innerHTML = newFooter.innerHTML;
          oldFooter.hidden = newFooter.hidden;
        }
      } catch (err) {
        console.warn('Drawer refresh failed, falling back to cart.js', err);
      }

      // Update count badge from cart.js
      try {
        const cart = await fetchJSON(routes.cart_url + '.js');
        this.updateCount(cart.item_count);
      } catch (err) {
        console.error('Cart count refresh failed:', err);
      }
    },

    updateCount(count) {
      if (!this.drawerCount) return;
      this.drawerCount.textContent = count;
      this.drawerCount.hidden = count === 0;
    }
  };

  // ========================================================
  // PRODUCT NAV — anchor tabs with scroll-spy
  // ========================================================
  const ProductNav = {
    init() {
      const nav = $('.product-nav');
      if (!nav) return;

      const links = $$('[data-product-nav-link]', nav);
      if (!links.length) return;

      const navHeight = nav.offsetHeight;

      // Smooth scroll, accounting for sticky header + nav height
      links.forEach((link) => {
        link.addEventListener('click', (e) => {
          const id = link.getAttribute('href').slice(1);
          const target = document.getElementById(id);
          if (!target) return;
          e.preventDefault();
          const top = target.getBoundingClientRect().top + window.scrollY - navHeight - 8;
          window.scrollTo({ top, behavior: 'smooth' });
        });
      });

      // Scroll-spy: highlight the tab for the section in view
      const targets = links
        .map((l) => document.getElementById(l.getAttribute('href').slice(1)))
        .filter(Boolean);

      if (!targets.length || !('IntersectionObserver' in window)) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const id = entry.target.id;
              links.forEach((l) =>
                l.classList.toggle('is-active', l.getAttribute('href') === '#' + id)
              );
            }
          });
        },
        { rootMargin: `-${navHeight + 20}px 0px -60% 0px`, threshold: 0 }
      );

      targets.forEach((t) => observer.observe(t));
    }
  };

  // ========================================================
  // CONTACT CARDS — open Tidio (or other live chat) on click
  // ========================================================
  const ContactChatTrigger = {
    init() {
      // Delegated so it works even if cards are added later.
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-open-chat]');
        if (!trigger) return;
        e.preventDefault();

        if (window.tidioChatApi && typeof window.tidioChatApi.show === 'function') {
          window.tidioChatApi.show();
          window.tidioChatApi.open();
        } else if (window.$crisp) {
          window.$crisp.push(['do', 'chat:open']);
        } else if (window.Intercom) {
          window.Intercom('show');
        } else if (window.zE) {
          window.zE('webWidget', 'open');
        } else {
          // No chat widget available yet — fall back to opening email.
          const mailto = document.querySelector('[href^="mailto:"]');
          if (mailto) {
            window.location.href = mailto.href;
          } else {
            alert('Live chat is still loading — please try again in a moment.');
          }
        }
      });
    }
  };

  // ========================================================
  // 9. PREMIUM CURSOR — dot + ring (Linear / Stripe style)
  // ========================================================
  const PremiumCursor = {
    dot: null, ring: null,
    mx: 0, my: 0, rx: 0, ry: 0,
    raf: null,

    init() {
      // Touch-only / coarse-pointer devices: keep native cursor
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

      this.dot = document.createElement('div');
      this.dot.className = 'cur-dot';
      this.dot.setAttribute('aria-hidden', 'true');

      this.ring = document.createElement('div');
      this.ring.className = 'cur-ring';
      this.ring.setAttribute('aria-hidden', 'true');

      document.body.append(this.dot, this.ring);

      // Dot snaps instantly to mouse position via left/top
      document.addEventListener('mousemove', (e) => {
        this.mx = e.clientX;
        this.my = e.clientY;
        this.dot.style.left = e.clientX + 'px';
        this.dot.style.top  = e.clientY + 'px';
      }, { passive: true });

      document.addEventListener('mouseleave', () => {
        this.dot.classList.add('cur--hidden');
        this.ring.classList.add('cur--hidden');
      });
      document.addEventListener('mouseenter', () => {
        this.dot.classList.remove('cur--hidden');
        this.ring.classList.remove('cur--hidden');
      });

      // Hover / click state on interactive elements
      const HOVER = 'a, button, [role="button"], input, select, textarea, label, .product-card, .btn, [data-gallery-thumb]';
      document.addEventListener('mouseover', (e) => {
        if (e.target.closest(HOVER)) {
          this.dot.classList.add('cur--hover');
          this.ring.classList.add('cur--hover');
        }
      });
      document.addEventListener('mouseout', (e) => {
        if (e.target.closest(HOVER)) {
          this.dot.classList.remove('cur--hover');
          this.ring.classList.remove('cur--hover');
        }
      });
      document.addEventListener('mousedown', () => {
        this.dot.classList.add('cur--click');
        this.ring.classList.add('cur--click');
      });
      document.addEventListener('mouseup', () => {
        this.dot.classList.remove('cur--click');
        this.ring.classList.remove('cur--click');
      });

      // Ring follows cursor with lerp (smooth lag)
      const tick = () => {
        this.rx += (this.mx - this.rx) * 0.11;
        this.ry += (this.my - this.ry) * 0.11;
        this.ring.style.left = this.rx + 'px';
        this.ring.style.top  = this.ry + 'px';
        this.raf = requestAnimationFrame(tick);
      };
      tick();
    }
  };

  // ========================================================
  // 10. SCROLL-DROP — water-drop fill progress indicator
  // ========================================================
  const ScrollDrop = {
    el: null,
    fillEl: null,
    id: 'sdclip' + Math.random().toString(36).slice(2, 6),

    init() {
      if (window.innerWidth < 1100) return;

      this.el = document.createElement('div');
      this.el.className = 'scroll-drop';
      this.el.setAttribute('aria-hidden', 'true');
      this.el.innerHTML =
        '<svg viewBox="0 0 26 34" xmlns="http://www.w3.org/2000/svg" width="26" height="34">' +
          '<defs>' +
            '<clipPath id="' + this.id + '">' +
              '<path d="M13 1C13 1 1 13 1 20.5C1 28 6.5 33 13 33C19.5 33 25 28 25 20.5C25 13 13 1 13 1Z"/>' +
            '</clipPath>' +
          '</defs>' +
          '<path d="M13 1C13 1 1 13 1 20.5C1 28 6.5 33 13 33C19.5 33 25 28 25 20.5C25 13 13 1 13 1Z"' +
          '      fill="none" stroke="var(--color-border)" stroke-width="1.5"/>' +
          '<rect class="scroll-drop__fill"' +
          '      x="0" y="34" width="26" height="34"' +
          '      clip-path="url(#' + this.id + ')"' +
          '      fill="var(--color-blue)" opacity="0.75"/>' +
        '</svg>';
      document.body.appendChild(this.el);

      this.fillEl = this.el.querySelector('.scroll-drop__fill');
      window.addEventListener('scroll', () => this.update(), { passive: true });
      this.update();
    },

    update() {
      if (!this.el) return;
      const scrolled  = window.scrollY;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const progress  = maxScroll > 0 ? Math.min(scrolled / maxScroll, 1) : 0;
      this.el.classList.toggle('is-visible', progress > 0.02);
      if (this.fillEl) {
        // y=34 = empty, y=0 = full
        this.fillEl.setAttribute('y', (34 * (1 - progress)).toFixed(2));
      }
    }
  };

  // ========================================================
  // 11. SCROLL REVEAL — fade-up on viewport entry
  // ========================================================
  const ScrollReveal = {
    observer: null,

    init() {
      if (!('IntersectionObserver' in window)) return;

      // Auto-tag common section elements for reveal animation
      const SELECTORS = [
        '.section-heading',
        '.section-subheading',
        '.eyebrow',
        '.product-card',
        '.feature-card',
        '.feature-item',
        '.testimonial-card',
        '.two-ways__item',
        '.image-text__content',
        '.pdp-row',
        '.faq-item',
        '.cs-waitlist',
      ];

      SELECTORS.forEach((sel) => {
        $$(sel).forEach((el, i) => {
          if (el.hasAttribute('data-reveal')) return;
          el.setAttribute('data-reveal', '');
          // Stagger siblings up to index 4
          if (i > 0 && i <= 4) {
            el.setAttribute('data-reveal-delay', String(i * 100));
          }
        });
      });

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-revealed');
              this.observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.07, rootMargin: '0px 0px -36px 0px' }
      );

      $$('[data-reveal]').forEach((el) => this.observer.observe(el));
    }
  };

  // ========================================================
  // 8. INIT ON DOM READY
  // ========================================================
  const ready = (fn) => {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  };

  ready(() => {
    Header.init();
    HeroSlideshow.init();
    TestimonialCarousel.init();
    ProductGallery.init();
    VariantPicker.init();
    QuantitySelectors.init();
    ProductNav.init();
    Cart.init();
    ContactChatTrigger.init();
    PremiumCursor.init();
    ScrollDrop.init();
    ScrollReveal.init();
  });
})();
